import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useLayerViewQuery } from "@/v2-backoffice-client/layer/view/layer.view.query"
import LayerDriftTab from "@/v2-backoffice-client/layer/drift/LayerDriftTab"
import LayerDriftTabBadge from "@/v2-backoffice-client/layer/drift/LayerDriftTabBadge"
import LayerDocumentDriftDot from "@/v2-backoffice-client/layer/drift/diagnostics/LayerDocumentDriftDot"
import LayerHistoryList from "@/v2-backoffice-client/layer/history/LayerHistoryList"
import LayerHistoryTabBadge from "@/v2-backoffice-client/layer/history/LayerHistoryTabBadge"
import { useLayerProbeOnOpen } from "@/v2-backoffice-client/layer/probe/useLayerProbeOnOpen"
import LayerUpdateEditor from "@/v2-backoffice-client/layer/update/LayerUpdateEditor"
import LayerViewDetails from "@/v2-backoffice-client/layer/view/LayerViewDetails"
import LayerViewInfo from "@/v2-backoffice-client/layer/view/LayerViewInfo"
import LayerViewToolbar from "@/v2-backoffice-client/layer/view/LayerViewToolbar"
import LayerAvailabilityTab from "@/v2-backoffice-client/layer/availability/LayerAvailabilityTab"
import {
  getLayerDocumentTabValue,
  LAYER_DOCUMENT_FIELDS,
  LAYER_VIEW_DEFAULT_TAB,
  LAYER_VIEW_DRIFT_TAB,
  LAYER_VIEW_TABS_ID,
} from "@/v2-backoffice-client/layer/layer.constants"
import {
  FEATURE_TAB,
  IS_CATALOG_AUTHORING_ENABLED,
} from "@/v2-backoffice-client/feature/feature.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

function LayerViewSheet() {
  const { id } = useAppViewSheetOpener()
  const featureCode = String(id)

  const { data, loading, error } = useLayerViewQuery({
    skip: !featureCode,
    variables: {
      input: {
        featureCode,
      },
    },
  })

  const layer = data?.getLayerByFeatureCode

  // One live probe per panel-open: fresh uptime verdict + drift
  // fingerprint, and first-time monitoring for layers the cron hasn't
  // discovered yet.
  const { isProbing } = useLayerProbeOnOpen(layer?.FeatureCode)

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading}>
          {layer?.Name ?? layer?.FeatureCode}
        </ViewSheet.Title>
      </ViewSheet.Header>

      {/* Uptime and customer visibility are items of the info row (details
          on hover) rather than cards of their own, so the editors below
          keep their height. */}
      <ViewSheet.HeaderRow>
        <LayerViewInfo
          layer={layer}
          isLoading={loading}
          isProbing={isProbing}
        />
        <LayerViewToolbar layer={layer} />
      </ViewSheet.HeaderRow>

      <Tabs
        id={LAYER_VIEW_TABS_ID}
        defaultValue={LAYER_VIEW_DEFAULT_TAB}
        isLoading={loading}
        error={error}
        isEmpty={!layer}
        emptyLabel="Layer not found."
      >
        <Tabs.List>
          <Tabs.Trigger value={FEATURE_TAB.details}>Details</Tabs.Trigger>
          <Tabs.Trigger value={FEATURE_TAB.availability}>
            Availability
          </Tabs.Trigger>
          {LAYER_DOCUMENT_FIELDS.map((field) => (
            <Tabs.Trigger key={field} value={getLayerDocumentTabValue(field)}>
              {field}
              {/* Marks the documents that still name something the layer
                  server changed, so drift is visible from the tab strip
                  and not only from the Drift tab. */}
              <LayerDocumentDriftDot
                featureCode={layer?.FeatureCode}
                field={field}
              />
            </Tabs.Trigger>
          ))}
          <Tabs.Trigger value={LAYER_VIEW_DRIFT_TAB}>
            Drift
            <LayerDriftTabBadge featureCode={layer?.FeatureCode} />
          </Tabs.Trigger>
          {/* The catalog audit trail lives in v2-features-db, which only
              exists on dev deployments. */}
          {IS_CATALOG_AUTHORING_ENABLED && (
            <Tabs.Trigger value="history">
              History
              <LayerHistoryTabBadge featureCode={layer?.FeatureCode} />
            </Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.Content value={FEATURE_TAB.details} className="flex-1">
          {layer && <LayerViewDetails layer={layer} />}
        </Tabs.Content>

        {/* Everything that decides whether a customer sees the layer, in
            the order the console checks it. */}
        <Tabs.Content value={FEATURE_TAB.availability} className="flex-1">
          {layer && <LayerAvailabilityTab layer={layer} />}
        </Tabs.Content>

        {LAYER_DOCUMENT_FIELDS.map((field) => (
          <Tabs.Content
            key={field}
            value={getLayerDocumentTabValue(field)}
            className="flex-1"
          >
            <LayerUpdateEditor field={field} layer={layer} />
          </Tabs.Content>
        ))}

        <Tabs.Content value={LAYER_VIEW_DRIFT_TAB} className="flex-1">
          <LayerDriftTab
            featureCode={layer?.FeatureCode}
            isProbing={isProbing}
          />
        </Tabs.Content>

        {IS_CATALOG_AUTHORING_ENABLED && (
          <Tabs.Content value="history" className="flex-1">
            {layer && <LayerHistoryList featureCode={layer.FeatureCode} />}
          </Tabs.Content>
        )}
      </Tabs>
    </>
  )
}

export default LayerViewSheet
